import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, Switch } from "antd";
import JsonView from "@uiw/react-json-view";
import { lightTheme } from "@uiw/react-json-view/light";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from "../components/StandardButton";

const JSONFormatter = () => {
  const [input, setInput] = useState('{"name":"John","age":30,"address":{"city":"New York","zip":"10001"},"hobbies":["reading","sports"]}');
  const [parsedJson, setParsedJson] = useState(null);
  const [interactive, setInteractive] = useState(true);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  useEffect(() => {
    const id = searchParams.get("share");
    if (id) {
      api.get(`/json-formatter/share/${id}`)
        .then((res) => {
          const data = res.data;
          if (data.input && data.result) {
            setInput(data.input);
            setParsedJson(JSON.parse(data.result));
          }
        })
        .catch(() => {
          toast.error(t("server_error"));
        });
    }
  }, []);

  const handleFormat = useCallback(async () => {
    if (!input.trim()) return;
    try {
      const response = await api.post(`/json-formatter`, { text: input });
      const data = response.data;
      if (data.result) {
        setParsedJson(JSON.parse(data.result));
      } else {
        toast.error(data.error || t("error"));
        setParsedJson(null);
      }
    } catch {
      toast.error(t("server_error"));
      setParsedJson(null);
    }
  }, [input, t]);

  const handleInputKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleFormat();
    }
  };

  const handleShare = async () => {
    try {
      const response = await api.post(`/json-formatter/share`, {
        input,
        result: JSON.stringify(parsedJson, null, 2),
      });
      const data = response.data;
      if (data.id) {
        const url = `${window.location.origin}/tools/json-formatter?share=${data.id}`;
        await navigator.clipboard.writeText(url);
        toast.success(t("share_success"));
      } else {
        toast.error(t("share_failed"));
      }
    } catch {
      toast.error(t("share_failed"));
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(parsedJson, null, 2));
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("json_formatter_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("json_formatter_intro")}</p>
            </div>
            <textarea
              rows={8}
              placeholder={t("json_formatter_placeholder")}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 font-mono text-sm"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
            />
            <StandardButton
              className="bg-indigo-600 hover:bg-indigo-700 w-full"
              onClick={handleFormat}
              type="secondary"
              disabled={!input.trim()}
            >
              {t("json_formatter_button")}
            </StandardButton>

            {parsedJson && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 relative overflow-x-auto">
                <div className="flex justify-between items-center mb-2">
                  <strong className="text-indigo-700">{t("output")}</strong>
                  <div className="flex items-center gap-2">
                    <strong className="text-indigo-700">{t("interactive_text")}</strong>
                    <Switch checked={interactive} onChange={() => setInteractive(!interactive)} />
                  </div>
                </div>

                {interactive ? (
                  <JsonView
                    value={parsedJson}
                    style={{ fontSize: 14 }}
                    theme={lightTheme}
                    displayDataTypes={false}
                    enableClipboard={false}
                  />
                ) : (
                  <pre className="text-sm bg-white p-4 rounded overflow-auto border border-gray-200">
                    {JSON.stringify(parsedJson, null, 2)}
                  </pre>
                )}

                <div className="mt-4 flex gap-2">
                  <StandardButton
                    size="small"
                    type="secondary"
                    onClick={handleCopy}
                  >
                    {t("copy")}
                  </StandardButton>

                  <StandardButton
                    size="small"
                    type="secondary"
                    onClick={handleShare}
                  >
                    {t("share")}
                  </StandardButton>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default JSONFormatter;